import { isDefined } from "@clipboard-health/util-ts";
import { useTu } from "@src/appV2/i18n/useTu";
import { Box, Checkbox, FormControlLabel, Stack } from "@mui/material";

import { InviteChargeRateRow } from "../../DailyView/components/InviteDrawer/InviteChargeRateRow";
import { IconButton } from "../../components/IconButton";
import { InfoBanner } from "../../components/InfoBanner/InfoBanner";
import { LoadingButton } from "../../components/LoadingButton";
import { Text } from "../../components/Text";

interface InviteFooterProps {
  /** Whether a professional is selected and the invite can be sent. */
  canSend: boolean;
  isSending: boolean;
  postToMarketplace: boolean;
  onPostToMarketplaceChange: (value: boolean) => void;
  onPostToMarketplaceInfo: () => void;
  onSend: () => void;
  /** Validation/state error surfaced inline above the Send button. */
  errorMessage?: string;
  /** Whether the hourly boost feature applies to this invite (regardless of loading state). */
  isChargeRateVisible?: boolean;
  /** Current hourly boost amount, in minor units (cents). */
  hourlyBonusInMinorUnits?: number;
  /** Maximum hourly boost allowed, in minor units (cents). */
  maxBoostInMinorUnits?: number;
  /** Professional's share of the hourly boost. */
  professionalBonusDisplay?: string;
  isChargeRateLoading?: boolean;
  onBoostChange?: (boostInMinorUnits: number) => void;
  /** Whether the current boost would push the workplace's weekly spend over its limit. */
  isBoostOverLimit?: boolean;
  boostOverageInMinorUnits?: number;
  boostLimitInMinorUnits?: number;
  isBoostIncreaseRequested?: boolean;
  onRequestBoostIncrease?: () => void;
  /** True for CBH admins, who are warned about spend-limit crossings but never blocked. */
  isAdmin?: boolean;
}

/**
 * Footer for the Add Shift panel's Invite mode: the optional inline
 * hourly-bonus row, the "post as open shift if declined" toggle, and the
 * Send button.
 */
export function InviteFooter(props: InviteFooterProps) {
  const { tu } = useTu("schedule");
  const {
    canSend,
    isSending,
    postToMarketplace,
    onPostToMarketplaceChange,
    onPostToMarketplaceInfo,
    onSend,
    errorMessage,
    isChargeRateVisible = false,
    hourlyBonusInMinorUnits = 0,
    maxBoostInMinorUnits,
    professionalBonusDisplay,
    isChargeRateLoading = false,
    onBoostChange,
    isBoostOverLimit = false,
    boostOverageInMinorUnits = 0,
    boostLimitInMinorUnits,
    isBoostIncreaseRequested = false,
    onRequestBoostIncrease,
    isAdmin = false,
  } = props;

  return (
    <Stack spacing={3}>
      {isChargeRateVisible &&
        isDefined(onBoostChange) &&
        (isChargeRateLoading || isDefined(maxBoostInMinorUnits)) && (
          <InviteChargeRateRow
            isBackgroundTransparent
            boostLimitInMinorUnits={boostLimitInMinorUnits}
            boostOverageInMinorUnits={boostOverageInMinorUnits}
            hourlyBonusInMinorUnits={hourlyBonusInMinorUnits}
            isAdmin={isAdmin}
            isBoostIncreaseRequested={isBoostIncreaseRequested}
            isBoostOverLimit={isBoostOverLimit}
            isChargeRateLoading={isChargeRateLoading}
            maxBoostInMinorUnits={maxBoostInMinorUnits}
            professionalBonusDisplay={professionalBonusDisplay}
            onBoostChange={onBoostChange}
            onRequestBoostIncrease={onRequestBoostIncrease}
          />
        )}

      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={postToMarketplace}
              size="small"
              onChange={(event) => {
                onPostToMarketplaceChange(event.target.checked);
              }}
            />
          }
          label={<Text variant="body2">{tu("Post as open shift if declined")}</Text>}
          sx={{ flex: 1, margin: 0 }}
        />
        <IconButton
          aria-label={tu("More information")}
          iconType="information"
          size="small"
          variant="muted"
          onClick={onPostToMarketplaceInfo}
        />
      </Box>

      {errorMessage && <InfoBanner message={errorMessage} variant="error" />}

      <LoadingButton
        fullWidth
        disabled={!canSend}
        isLoading={isSending}
        variant="primary"
        onClick={onSend}
      >
        {tu("Send invite")}
      </LoadingButton>
    </Stack>
  );
}
